import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Suspense, lazy } from "react";
import { AuthProvider } from "./contexts/AuthContext";
import { RealtimeProviderWrapper } from "./contexts/RealtimeContext";
import { ClienteAuthProvider } from "./contexts/ClienteAuthContext";
import { EntregadorAuthProvider } from "./contexts/EntregadorAuthContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { RoleRedirect } from "./components/RoleRedirect";
import { AdminLayout } from "./layouts/AdminLayout";
import { PageSkeleton } from "./components/ui/page-skeleton";
import { UpdateBanner } from "./components/UpdateBanner";

// Lazy load main app pages for better code splitting
const Index = lazy(() => import("./pages/Index"));
const Auth = lazy(() => import("./pages/Auth"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Import build info para log no console
import '@/lib/buildInfo';

// Lazy load das páginas admin (named exports)
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard").then(m => ({ default: m.AdminDashboard })));
const AdminOperadores = lazy(() => import("./pages/admin/AdminOperadores").then(m => ({ default: m.AdminOperadores })));
const AdminVendas = lazy(() => import("./pages/admin/AdminVendas").then(m => ({ default: m.AdminVendas })));
const AdminRanking = lazy(() => import("./pages/admin/AdminRanking").then(m => ({ default: m.AdminRanking })));
const AdminFidelidade = lazy(() => import("./pages/admin/AdminFidelidade").then(m => ({ default: m.AdminFidelidade })));
const AdminCheckins = lazy(() => import("./pages/admin/AdminCheckins").then(m => ({ default: m.AdminCheckins })));
const AdminUso = lazy(() => import("./pages/admin/AdminUso").then(m => ({ default: m.AdminUso })));
const AdminConfig = lazy(() => import("./pages/admin/AdminConfig").then(m => ({ default: m.AdminConfig })));
const AdminIA = lazy(() => import("./pages/admin/AdminIA").then(m => ({ default: m.AdminIA })));
const AdminWhatsApp = lazy(() => import("./pages/admin/AdminWhatsApp").then(m => ({ default: m.AdminWhatsApp })));
const AdminAvaliacoes = lazy(() => import("./pages/admin/AdminAvaliacoes"));
const AdminFaleConosco = lazy(() => import("./pages/admin/AdminFaleConosco").then(m => ({ default: m.AdminFaleConosco })));
const AdminTreinos = lazy(() => import("./pages/admin/AdminTreinos").then(m => ({ default: m.AdminTreinos })));
const AdminInfluencers = lazy(() => import("./pages/admin/AdminInfluencers").then(m => ({ default: m.AdminInfluencers })));

// Influencer pages
const InfluencerDashboard = lazy(() => import("./pages/influencer/InfluencerDashboard"));
const InfluencerLanding = lazy(() => import("./pages/InfluencerLanding"));

// Lazy load das páginas públicas de cliente
const CardapioPublico = lazy(() => import("./pages/cliente/CardapioPublico"));
const CarrinhoCliente = lazy(() => import("./pages/cliente/CarrinhoCliente"));
const ConfirmacaoPedido = lazy(() => import("./pages/cliente/ConfirmacaoPedido"));
const AvaliacaoCliente = lazy(() => import("./pages/cliente/AvaliacaoCliente"));
const ClienteLogin = lazy(() => import("./pages/cliente/ClienteLogin"));
const ClienteCadastro = lazy(() => import("./pages/cliente/ClienteCadastro"));
const ClienteHome = lazy(() => import("./pages/cliente/ClienteHome"));
const ClienteLoginGeral = lazy(() => import("./pages/cliente/ClienteLoginGeral"));
const ClienteCadastroGeral = lazy(() => import("./pages/cliente/ClienteCadastroGeral"));
const ClientePedidos = lazy(() => import("./pages/cliente/ClientePedidos"));
const MeusPedidos = lazy(() => import("./pages/cliente/MeusPedidos"));
// ContaCliente removido - funcionalidade unificada em ClienteHome
const TrackingPedido = lazy(() => import("./pages/cliente/TrackingPedido"));
const CardapioToken = lazy(() => import("./pages/cliente/CardapioToken"));
const CardapioMesa = lazy(() => import("./pages/cliente/CardapioMesa"));

// Lazy load das páginas do entregador
const EntregadorLogin = lazy(() => import("./pages/entregador/EntregadorLogin"));
const EntregadorDashboard = lazy(() => import("./pages/entregador/EntregadorDashboard"));

// Lazy load da página de diagnóstico (dev only)
const DevDiagnostics = lazy(() => import("./pages/DevDiagnostics"));
const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <RealtimeProviderWrapper>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <UpdateBanner />
          <BrowserRouter>
            <main id="main-content">
              <Routes>
              {/* Public auth route */}
                <Route path="/login" element={
                  <Suspense fallback={<PageSkeleton />}>
                    <Auth />
                  </Suspense>
                } />
                
                {/* Login geral do cliente (sem unidade) */}
                <Route path="/cliente/login" element={
                  <ClienteAuthProvider>
                    <Suspense fallback={<PageSkeleton />}>
                      <ClienteLoginGeral />
                    </Suspense>
                  </ClienteAuthProvider>
                } />
                <Route path="/cliente/cadastro" element={
                  <ClienteAuthProvider>
                    <Suspense fallback={<PageSkeleton />}>
                      <ClienteCadastroGeral />
                    </Suspense>
                  </ClienteAuthProvider>
                } />
                
                {/* Cliente Home - página principal após login */}
                <Route path="/u/home" element={
                  <ClienteAuthProvider>
                    <Suspense fallback={<PageSkeleton />}>
                      <ClienteHome />
                    </Suspense>
                  </ClienteAuthProvider>
                } />
                <Route path="/u/home/pedidos" element={
                  <ClienteAuthProvider>
                    <Suspense fallback={<PageSkeleton />}>
                      <ClientePedidos />
                    </Suspense>
                  </ClienteAuthProvider>
                } />
                
                {/* Cardápio por token temporário (modo variável) */}
                <Route path="/cardapio/t/:token" element={
                  <ClienteAuthProvider>
                    <Suspense fallback={<PageSkeleton />}>
                      <CardapioToken />
                    </Suspense>
                  </ClienteAuthProvider>
                } />
                
                {/* Cardápio por mesa com senha (QR fixo) */}
                <Route path="/u/:slug/mesa/:clienteId" element={
                  <ClienteAuthProvider>
                    <Suspense fallback={<PageSkeleton />}>
                      <CardapioMesa />
                    </Suspense>
                  </ClienteAuthProvider>
                } />
                
                {/* Public client routes (cardápio digital) - wrapped with ClienteAuthProvider */}
                <Route path="/u/:slug" element={
                  <ClienteAuthProvider>
                    <Suspense fallback={<PageSkeleton />}>
                      <CardapioPublico />
                    </Suspense>
                  </ClienteAuthProvider>
                } />
                <Route path="/u/:slug/login" element={
                  <ClienteAuthProvider>
                    <Suspense fallback={<PageSkeleton />}>
                      <ClienteLogin />
                    </Suspense>
                  </ClienteAuthProvider>
                } />
                <Route path="/u/:slug/cadastro" element={
                  <ClienteAuthProvider>
                    <Suspense fallback={<PageSkeleton />}>
                      <ClienteCadastro />
                    </Suspense>
                  </ClienteAuthProvider>
                } />
                {/* Redireciona /u/:slug/conta para /u/home */}
                <Route path="/u/:slug/conta" element={
                  <ClienteAuthProvider>
                    <Navigate to="/u/home" replace />
                  </ClienteAuthProvider>
                } />
                <Route path="/u/:slug/pedidos" element={
                  <ClienteAuthProvider>
                    <Suspense fallback={<PageSkeleton />}>
                      <MeusPedidos />
                    </Suspense>
                  </ClienteAuthProvider>
                } />
                <Route path="/u/:slug/carrinho" element={
                  <ClienteAuthProvider>
                    <Suspense fallback={<PageSkeleton />}>
                      <CarrinhoCliente />
                    </Suspense>
                  </ClienteAuthProvider>
                } />
                <Route path="/u/:slug/confirmacao/:pedidoId" element={
                  <ClienteAuthProvider>
                    <Suspense fallback={<PageSkeleton />}>
                      <ConfirmacaoPedido />
                    </Suspense>
                  </ClienteAuthProvider>
                } />
                <Route path="/u/:slug/tracking/:pedidoId" element={
                  <Suspense fallback={<PageSkeleton />}>
                    <TrackingPedido />
                  </Suspense>
                } />
                <Route path="/u/:slug/avaliar/:token" element={
                  <ClienteAuthProvider>
                    <Suspense fallback={<PageSkeleton />}>
                      <AvaliacaoCliente />
                    </Suspense>
                  </ClienteAuthProvider>
                } />
                
                {/* Portal do Entregador */}
                <Route path="/entregador" element={
                  <EntregadorAuthProvider>
                    <Suspense fallback={<PageSkeleton />}>
                      <EntregadorLogin />
                    </Suspense>
                  </EntregadorAuthProvider>
                } />
                <Route path="/entregador/dashboard" element={
                  <EntregadorAuthProvider>
                    <Suspense fallback={<PageSkeleton />}>
                      <EntregadorDashboard />
                    </Suspense>
                  </EntregadorAuthProvider>
                } />
                
                {/* Influencer public landing */}
                <Route path="/i/:slug" element={
                  <Suspense fallback={<PageSkeleton />}>
                    <InfluencerLanding />
                  </Suspense>
                } />
                
                {/* Influencer dashboard (accessible by operators) */}
                <Route path="/influencer" element={
                  <ProtectedRoute allowedRoles={['operador', 'vendedor']}>
                    <Suspense fallback={<PageSkeleton />}>
                      <InfluencerDashboard />
                    </Suspense>
                  </ProtectedRoute>
                } />
                
                {/* Root redirect based on role */}
                <Route path="/" element={<RoleRedirect />} />
                
                {/* Admin routes with nested layout */}
                <Route path="/admin" element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <AdminLayout />
                  </ProtectedRoute>
                }>
                  <Route index element={
                    <Suspense fallback={<PageSkeleton />}>
                      <AdminDashboard />
                    </Suspense>
                  } />
                  <Route path="checkins" element={
                    <Suspense fallback={<PageSkeleton />}>
                      <AdminCheckins />
                    </Suspense>
                  } />
                  <Route path="operadores" element={
                    <Suspense fallback={<PageSkeleton />}>
                      <AdminOperadores />
                    </Suspense>
                  } />
                  <Route path="avaliacoes" element={
                    <Suspense fallback={<PageSkeleton />}>
                      <AdminAvaliacoes />
                    </Suspense>
                  } />
                  <Route path="treinos" element={
                    <Suspense fallback={<PageSkeleton />}>
                      <AdminTreinos />
                    </Suspense>
                  } />
                  <Route path="config" element={
                    <Suspense fallback={<PageSkeleton />}>
                      <AdminConfig />
                    </Suspense>
                  } />
                  <Route path="ia" element={
                    <Suspense fallback={<PageSkeleton />}>
                      <AdminIA />
                    </Suspense>
                  } />
                  <Route path="whatsapp" element={
                    <Suspense fallback={<PageSkeleton />}>
                      <AdminWhatsApp />
                    </Suspense>
                  } />
                  <Route path="fale-conosco" element={
                    <Suspense fallback={<PageSkeleton />}>
                      <AdminFaleConosco />
                    </Suspense>
                  } />
                  <Route path="influencers" element={
                    <Suspense fallback={<PageSkeleton />}>
                      <AdminInfluencers />
                    </Suspense>
                  } />
                  <Route path="diagnostics" element={
                    <Suspense fallback={<div className="p-4">Carregando...</div>}>
                      <DevDiagnostics />
                    </Suspense>
                  } />
                </Route>
                
                {/* Main app - operador has full access, vendedor has restricted access */}
                <Route path="/app" element={
                  <ProtectedRoute allowedRoles={['operador', 'vendedor']}>
                    <Suspense fallback={<PageSkeleton />}>
                      <Index />
                    </Suspense>
                  </ProtectedRoute>
                } />
                
                {/* Legacy redirect for old dev diagnostics route */}
                <Route path="/dev/diagnostics" element={
                  <Suspense fallback={<PageSkeleton />}>
                    <NotFound />
                  </Suspense>
                } />
                
                {/* Catch-all */}
                <Route path="*" element={
                  <Suspense fallback={<PageSkeleton />}>
                    <NotFound />
                  </Suspense>
                } />
              </Routes>
            </main>
            
          </BrowserRouter>
        </TooltipProvider>
      </RealtimeProviderWrapper>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
